export const PAYMENT_PROVIDERS = {
    wave: {
        label: 'Wave',
        description: 'Wave Checkout : le candidat est redirigé vers l’application Wave. Confirmation par webhook signé (Wave-Signature).',
        refundable: true,
        fields: [
            {
                key: 'apiKey',
                label: 'Clé API (Checkout)',
                secret: true,
                required: true,
                help: 'Clé « wave_sn_prod_… » créée dans le portail Wave Business, avec l’accès Checkout.',
            },
            {
                key: 'webhookSecret',
                label: 'Secret du webhook',
                secret: true,
                required: true,
                help: 'Secret de signature du webhook (méthode « Signing secret ») affiché par Wave à la création du webhook.',
            },
            {
                key: 'signingSecret',
                label: 'Secret de signature des requêtes',
                secret: true,
                required: false,
                help: 'Uniquement si la clé API a été créée avec la signature des requêtes activée.',
            },
        ],
    },
    orange_money: {
        label: 'Orange Money',
        description: 'Orange Money (Sonatel) : lien de paiement Max it / Orange Money et QR code. Notification de fin de paiement, puis contrôle du statut auprès d’Orange.',
        refundable: false,
        fields: [
            {
                key: 'clientId',
                label: 'Client ID (OAuth)',
                secret: false,
                required: true,
            },
            {
                key: 'clientSecret',
                label: 'Client secret (OAuth)',
                secret: true,
                required: true,
            },
            {
                key: 'merchantCode',
                label: 'Code marchand',
                secret: false,
                required: true,
                pattern: '^\\d{6}$',
                help: '6 chiffres.',
            },
            {
                key: 'merchantName',
                label: 'Nom du marchand',
                secret: false,
                required: true,
                help: 'Nom affiché au candidat dans Max it / Orange Money.',
            },
            {
                key: 'callbackApiKey',
                label: 'Clé de notification',
                secret: true,
                required: true,
                help: 'Secret partagé enregistré auprès d’Orange avec l’URL de notification ; Orange le renvoie dans l’en-tête Authorization de chaque notification.',
            },
            {
                key: 'apiKey',
                label: 'X-Api-Key marchand',
                secret: true,
                required: false,
                help: 'Seulement si votre contrat en fournit une.',
            },
            {
                key: 'qrValidity',
                label: 'Validité du lien (secondes)',
                secret: false,
                required: false,
                type: 'number',
                pattern: '^\\d{2,5}$',
                help: '900 par défaut (15 minutes), 86 400 au maximum.',
            },
        ],
    },
    fake: {
        label: 'Simulateur',
        description: 'Paiement simulé pour le développement et la recette : aucun argent ne circule. Interdit en production.',
        refundable: true,
        testOnly: true,
        fields: [],
    },
};
export const PAYMENT_PROVIDER_CODES = Object.keys(PAYMENT_PROVIDERS);
export const PAYMENT_ENVIRONMENTS = {
    sandbox: 'Test (sandbox)',
    live: 'Production (réel)',
};
/** URLs des API des prestataires (le connecteur choisit selon l'environnement). */
export const PROVIDER_BASE_URLS = {
    wave: { sandbox: 'https://api.wave.com', live: 'https://api.wave.com' },
    orange_money: {
        sandbox: 'https://api.sandbox.orange-sonatel.com',
        live: 'https://api.orange-sonatel.com',
    },
};
/* ------------------------------------------------------------------ */
/* Montants                                                            */
/* ------------------------------------------------------------------ */
/** Frais du moyen de paiement : fixe + pourcentage, arrondi à l'unité supérieure (XOF). */
export function paymentFees(amount, fixed, percent) {
    const variable = Math.ceil((amount * percent) / 100 - 1e-9);
    return Math.max(0, Math.round(fixed) + Math.max(0, variable));
}
/** « 5 000 F CFA ». */
export function formatAmount(value, currency = 'XOF') {
    const n = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 })
        .format(value)
        .replace(/\u202f|\u00a0/g, ' ');
    return currency === 'XOF' ? `${n} F CFA` : `${n} ${currency}`;
}
/* ------------------------------------------------------------------ */
/* Quand payer (ADR-001)                                               */
/* ------------------------------------------------------------------ */
/** États du dossier dans lesquels le paiement est attendu, selon le concours. */
export const PAYABLE_STATES = {
    before_submit: ['draft', 'ready'],
    after_submit: ['submitted', 'under_review', 'to_complete'],
    after_admissibility: ['admissible'],
    not_required: [],
};
/** États de paiement à partir desquels un (nouveau) paiement peut être lancé. */
export const PAYMENT_OPEN_STATES = [
    'pending',
    'processing',
    'failed',
    'cancelled',
];
export const PAYABILITY_MESSAGES = {
    not_required: 'Aucun paiement n’est demandé pour ce concours.',
    already_paid: 'Les frais d’inscription sont déjà payés.',
    not_yet: 'Le paiement n’est pas encore ouvert pour ce dossier.',
    closed: 'Ce dossier ne peut plus faire l’objet d’un paiement.',
    deadline_passed: 'La date limite de paiement est dépassée.',
    refunded: 'Le paiement de ce dossier a été remboursé.',
};
/** Le candidat peut-il payer maintenant ? `null` = oui. */
export function payability(input) {
    if (input.timing === 'not_required' || input.fee <= 0 || input.paymentStatus === 'not_required')
        return 'not_required';
    if (input.paymentStatus === 'confirmed')
        return 'already_paid';
    if (input.paymentStatus === 'refunded')
        return 'refunded';
    if (['withdrawn', 'cancelled', 'not_admissible'].includes(input.applicationStatus))
        return 'closed';
    if (!PAYABLE_STATES[input.timing].includes(input.applicationStatus))
        return 'not_yet';
    if (input.deadline && new Date(input.deadline) < (input.now ?? new Date()))
        return 'deadline_passed';
    return null;
}
/* ------------------------------------------------------------------ */
/* Transactions                                                        */
/* ------------------------------------------------------------------ */
/** Référence de transaction : « PAY-K7Q2A4M9XZ » (alphabet des matricules). */
export const PAYMENT_REFERENCE_PREFIX = 'PAY';
export const PAYMENT_REFERENCE_LENGTH = 10;
/** Écart constaté lors d'une notification ou d'un rapprochement. */
export const PAYMENT_ANOMALIES = {
    amount_mismatch: 'Montant différent de celui attendu',
    currency_mismatch: 'Devise différente',
    reference_mismatch: 'Référence inconnue ou différente',
    duplicate_transaction: 'Transaction déjà rattachée à un autre paiement',
    late_success: 'Paiement réussi après un échec ou une annulation',
    not_found_at_provider: 'Transaction introuvable chez le prestataire',
    status_mismatch: 'Statut différent chez le prestataire',
};
export const PAYMENT_EVENT_OUTCOMES = {
    confirmed: 'Paiement confirmé',
    failed: 'Échec enregistré',
    ignored: 'Ignoré (sans effet)',
    duplicate: 'Doublon (déjà traité)',
    rejected: 'Rejeté (authenticité)',
    anomaly: 'Anomalie à examiner',
    error: 'Erreur de traitement',
};
//# sourceMappingURL=payments.js.map